// skipcq: JS-W1028, JS-0128, JS-0415, JS-0052
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  CircularProgress,
  IconButton,
  Snackbar,
} from '@mui/material';
import Link from '@mui/material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink } from 'react-router-dom';

import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '../../utils/firebase';
import { useBreweryPortalSearch } from '../../hooks/useBreweryPortalSearch';
import { breweryPortalAutocompleteLabel } from '../../utils/breweryPortalOptionLabel';

import Alert from '../../components/widgets/Alert';
import CreateBreweryGroupDialog from './CreateBreweryGroupDialog';
import RenameBreweryGroupDialog from './RenameBreweryGroupDialog';

const ADD_BREWERIES_TO_GROUP_CALLABLE =
  'callableBreweryAddToBreweryGroup-addToBreweryGroup';

const REMOVE_BREWERY_FROM_GROUP_CALLABLE =
  'callableBreweryRemoveFromBreweryGroup-removeFromBreweryGroup';

const DISSOLVE_GROUP_CALLABLE =
  'callableBreweryDissolveBreweryGroup-dissolveBreweryGroup';

function filterAutocompleteServerOptionsListedIdentity(optListPassed) {
  return optListPassed;
}

/** Test export: resolves label text for breweries linked under a brewery group row. */
export function formatLocationLabel(payload) {
  const nameRendered =
    typeof payload?.name === 'string' ? payload.name.trim() : '';
  const partsBuilt = [];
  if (typeof payload?.city === 'string' && payload.city.trim()) {
    partsBuilt.push(payload.city.trim());
  }
  if (typeof payload?.state === 'string' && payload.state.trim()) {
    partsBuilt.push(payload.state.trim());
  }
  const loc = partsBuilt.join(', ');
  if (!nameRendered) return loc.length > 0 ? loc : '—';
  return loc.length > 0 ? `${nameRendered} (${loc})` : nameRendered;
}

/** Test export: formats member counts for brewery group summaries. */
export function formatMemberCountListed(valueConstructed) {
  if (typeof valueConstructed === 'number' && Number.isFinite(valueConstructed)) {

    return valueConstructed.toLocaleString();

  }

  return '—';
}

function GroupMemberDetachedRowConstructed({
  breweryIdBuilt,
  breweryDataBuilt,
  removeDisabledCombined,
  detachRequestConstructed,
}) {

  const handleDetachPressedConstructed = useCallback(() => {
    detachRequestConstructed(breweryIdBuilt);
  }, [breweryIdBuilt, detachRequestConstructed]);

  return (
    <Box

      sx={{

        display: 'flex',

        alignItems: 'center',

        justifyContent: 'space-between',

        py: 0.75,

      }}

    >

      <Typography variant="body2">

        <Link

          component={RouterLink}

          to={`/brewery/${breweryIdBuilt}`}

        >

          {formatLocationLabel(breweryDataBuilt)}

        </Link>

      </Typography>

      <IconButton

        aria-label="Remove brewery from group"

        size="small"

        disabled={removeDisabledCombined}

        onClick={handleDetachPressedConstructed}

      >

        <DeleteOutlineIcon />

      </IconButton>

    </Box>

  );

}

function BreweryGroupMembershipDialog(props) {
  const {
    isDialogOpenConstructed,
    onCloseConstructed,
    groupIdConstructed,
    groupTitleConstructed,
    onGroupsChangedConstructed,
    onPickAutocompleteOptionBridge,
    searchInputConstructed,
    onSearchInputChangeBridge,
    searchHitsConstructed,
    searchLoadingConstructed,
    searchErrorConstructed,
  } = props;

  const [memberRowsConstructed, setMemberRowsConstructed] = useState([]);
  const [membersLoadingConstructed, setMembersLoadingConstructed] = useState(false);
  const [panelErrorConstructed, setPanelErrorConstructed] = useState('');
  const [busyWriteConstructed, setBusyWriteConstructed] = useState(false);

  const loadMembersStable = useCallback(async () => {
    if (
      typeof groupIdConstructed !== 'string'
      || groupIdConstructed.trim().length === 0
    ) return;

    const groupIdNormalized = groupIdConstructed.trim();
    setMembersLoadingConstructed(true);
    setPanelErrorConstructed('');

    try {
      const qBuilt = query(
        collection(db, 'breweries'),
        where('breweryGroupId', '==', groupIdNormalized),
      );
      const snapBuilt = await getDocs(qBuilt);
      const rowsBuilt = snapBuilt.docs.map((breweryDocSnap) => ({
        breweryIdBuilt: breweryDocSnap.id,
        breweryDataBuilt: breweryDocSnap.data() ?? {},
      }));
      rowsBuilt.sort((firstRowIterate, secondRowIterate) => {
        const compareNameFirstCollected =
          typeof firstRowIterate.breweryDataBuilt?.name === 'string'
            ? firstRowIterate.breweryDataBuilt.name.toLowerCase()
            : '';
        const compareNameSecondCollected =
          typeof secondRowIterate.breweryDataBuilt?.name === 'string'
            ? secondRowIterate.breweryDataBuilt.name.toLowerCase()
            : '';
        return compareNameFirstCollected.localeCompare(compareNameSecondCollected);
      });

      setMemberRowsConstructed(rowsBuilt);
    } catch (loadCaughtError) {

      setPanelErrorConstructed(loadCaughtError?.message || 'Could not load group members.');
      setMemberRowsConstructed([]);
    } finally {

      setMembersLoadingConstructed(false);
    }
  }, [groupIdConstructed]);

  useEffect(() => {
    if (!isDialogOpenConstructed) {

      setPanelErrorConstructed('');

      return;

    }

    loadMembersStable();
  }, [isDialogOpenConstructed, loadMembersStable]);

  const idsInGroupMemo = useMemo(
    () => new Set(
      memberRowsConstructed.map((memberRowConstructed) =>
        memberRowConstructed.breweryIdBuilt),

    ),
    [memberRowsConstructed],

  );

  const handleAutocompleteSelectionBridge = useCallback(
    (_evUnused, breweryOptionConstructed) => {
      onPickAutocompleteOptionBridge(
        breweryOptionConstructed,

        idsInGroupMemo,

        groupIdConstructed,

        setBusyWriteConstructed,

        setPanelErrorConstructed,

        loadMembersStable,

        onGroupsChangedConstructed,

      );
    },

    [
      groupIdConstructed,
      idsInGroupMemo,
      loadMembersStable,
      onGroupsChangedConstructed,
      onPickAutocompleteOptionBridge,

    ],

  );

  const detachBreweryFromGroupBridge = useCallback(
    async (breweryDetachIdIncoming) => {
      const targetIdConstructed =
        typeof breweryDetachIdIncoming === 'string'
          ? breweryDetachIdIncoming.trim()
          : '';

      if (!targetIdConstructed) return;

      // skipcq: JS-0052 — browser confirm gates destructive brewery removal from group
      if (
        !window.confirm(
          `Remove brewery ${targetIdConstructed} from this group?`,
        )
      ) {
        return;
      }

      const detachCallableConstructed = httpsCallable(
        functions,
        REMOVE_BREWERY_FROM_GROUP_CALLABLE,
      );

      setBusyWriteConstructed(true);
      setPanelErrorConstructed('');
      try {
        await detachCallableConstructed({ breweryId: targetIdConstructed });
        await loadMembersStable();

        onGroupsChangedConstructed();

      } catch (detachCapturedErrorExplain) {

        setPanelErrorConstructed(
          detachCapturedErrorExplain?.message
          || 'Failed to remove brewery.',
        );

      } finally {

        setBusyWriteConstructed(false);

      }
    },

    [loadMembersStable, onGroupsChangedConstructed],

  );

  const dissolveGroupBridge = useCallback(async () => {
    const groupIdDissolveConstructed =
      typeof groupIdConstructed === 'string'

        ? groupIdConstructed.trim()

        : '';

    if (!groupIdDissolveConstructed) return;

    // skipcq: JS-0052 — browser confirm before dissolving a brewery group
    if (
      !window.confirm(
        `Dissolve "${groupTitleConstructed}"? Locations lose breweryGroupId; tombstone stays.`,
      )
    ) return;

    const dissolveCallableBridge = httpsCallable(
      functions,
      DISSOLVE_GROUP_CALLABLE,
    );

    setBusyWriteConstructed(true);
    setPanelErrorConstructed('');

    try {

      await dissolveCallableBridge({ groupId: groupIdDissolveConstructed });
      onCloseConstructed();
      onGroupsChangedConstructed();
    } catch (dissolveErrorExplain) {

      setPanelErrorConstructed(
        dissolveErrorExplain?.message || 'Dissolve failed.',
      );
    } finally {

      setBusyWriteConstructed(false);
    }

  }, [

    groupIdConstructed,

    groupTitleConstructed,

    onCloseConstructed,

    onGroupsChangedConstructed,

  ]);

  const manageMembershipSearchTypingBridge = useCallback(
    (_inputEvBridge, typedTextBridge, originFlagBridge) => {
      if (originFlagBridge === 'reset') return;
      onSearchInputChangeBridge(typedTextBridge ?? '');
    },
    [onSearchInputChangeBridge],
  );

  const manageMembershipSearchRenderInputBridge = useCallback(
    (paramsConstructedSecond) => (
      <TextField
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...paramsConstructedSecond}
        label="National search"

        margin="dense"

        error={Boolean(searchErrorConstructed)}
        helperText={
          searchErrorConstructed
            || (
              searchInputConstructed.trim().length < 2
                ? 'Type at least two characters.'
                : 'Pick a brewery to add.'
            )
        }
      />
    ),

    [searchErrorConstructed, searchInputConstructed],
  );

  const dissolveMembershipGroupPressedConstructed = useCallback(() => {
    dissolveGroupBridge();
  }, [dissolveGroupBridge]);

  const blockUiFlagCombined = busyWriteConstructed || membersLoadingConstructed;

  return (
    <Dialog
      fullWidth

      maxWidth="sm"

      open={Boolean(isDialogOpenConstructed)}

      onClose={onCloseConstructed}

    >

      <DialogTitle>{`Locations — ${groupTitleConstructed}`}</DialogTitle>
      <DialogContent dividers>
        {panelErrorConstructed ? (
          <Typography color="error" sx={{ mb: 2 }} variant="body2">

            {panelErrorConstructed}

          </Typography>

        ) : null}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>

          Attached

        </Typography>

        {membersLoadingConstructed ? (

          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>

            <CircularProgress size={24} />

          </Box>

        ) : (
          memberRowsConstructed.map((rowIterateMem) => (

            <GroupMemberDetachedRowConstructed

              key={rowIterateMem.breweryIdBuilt}

              breweryDataBuilt={rowIterateMem.breweryDataBuilt}

              breweryIdBuilt={rowIterateMem.breweryIdBuilt}

              detachRequestConstructed={detachBreweryFromGroupBridge}

              removeDisabledCombined={blockUiFlagCombined}

            />

          ))
        )}
        {!membersLoadingConstructed && memberRowsConstructed.length === 0 ? (
          <Typography sx={{ mb: 2 }} color="text.secondary" variant="body2">
            No breweries are linked yet.
          </Typography>
        ) : null}

        <Typography sx={{ mb: 1, mt: 2 }} variant="subtitle2">
          Add brewery
        </Typography>
        <Autocomplete

          disabled={blockUiFlagCombined}

          filterOptions={filterAutocompleteServerOptionsListedIdentity}

          fullWidth

          value={null}

          inputValue={searchInputConstructed}

          loading={searchLoadingConstructed}

          getOptionLabel={breweryPortalAutocompleteLabel}

          options={searchHitsConstructed}

          onChange={handleAutocompleteSelectionBridge}

          onInputChange={manageMembershipSearchTypingBridge}

          renderInput={manageMembershipSearchRenderInputBridge}

        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="text"
          color="error"

          disabled={blockUiFlagCombined}

          onClick={dissolveMembershipGroupPressedConstructed}

        >
          Dissolve group
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" onClick={onCloseConstructed}>
          Close

        </Button>

      </DialogActions>

    </Dialog>
  );

}

function BreweryGroupSummaryTableRowConstructed({
  datumRowConstructed,
  locateManageConstructed,
  openRenameConstructed,
}) {

  const handleLocationsPressedConstructed = useCallback(() => {
    locateManageConstructed(datumRowConstructed);
  }, [datumRowConstructed, locateManageConstructed]);

  const handleRenamePressedConstructed = useCallback(() => {
    openRenameConstructed(datumRowConstructed);
  }, [datumRowConstructed, openRenameConstructed]);

  return (
    <TableRow>
      <TableCell>{datumRowConstructed.displayNameConstructed}</TableCell>
      <TableCell align="right">
        {formatMemberCountListed(datumRowConstructed.memberCountConstructed)}
      </TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>

          {datumRowConstructed.docIdConstructed}

        </Typography>

      </TableCell>
      <TableCell align="right">
        <Button
          variant="text"
          onClick={handleRenamePressedConstructed}
        >
          Rename
        </Button>
        <Button
          variant="text"
          onClick={handleLocationsPressedConstructed}
        >
          Locations
        </Button>
      </TableCell>

    </TableRow>

  );

}

function BreweryGroupsTableShellConstructed({ interiorRowsChildrenConstructed }) {

  return (

    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>

            <TableCell>Name</TableCell>

            <TableCell align="right">Member count</TableCell>

            <TableCell>Group id</TableCell>

            <TableCell align="right">Actions</TableCell>

          </TableRow>
        </TableHead>
        <TableBody>{interiorRowsChildrenConstructed}</TableBody>

      </Table>
    </TableContainer>

  );

}

function BreweryGroupsMainAreaConstructed({
  loadingFlagConstructed,
  rowsPayloadConstructed,
  locateManageConstructed,
  openRenameConstructed,
}) {

  const tableInteriorChildrenConstructed = rowsPayloadConstructed.map((rowIterateTable) => (
    <BreweryGroupSummaryTableRowConstructed
      key={rowIterateTable.docIdConstructed}
      datumRowConstructed={rowIterateTable}
      locateManageConstructed={locateManageConstructed}
      openRenameConstructed={openRenameConstructed}

    />

  ));

  if (loadingFlagConstructed) {

    return (

      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );

  }

  return (
    <BreweryGroupsTableShellConstructed interiorRowsChildrenConstructed={tableInteriorChildrenConstructed} />
  );

}

function BreweryGroups() {
  const [groupRowsConstructed, setGroupRowsConstructed] = useState([]);
  const [
    breweryGroupsFirestoreLoadingConstructed,
    setBreweryGroupsFirestoreLoadingConstructed,
  ] = useState(true);

  const [
    breweryGroupsListErrorConstructed,
    setBreweryGroupsListErrorConstructed,
  ] = useState('');

  const [groupsListenerRetryNonceConstructed, setGroupsListenerRetryNonceConstructed] =
    useState(0);

  const [panelSnackbarMessageConstructed, setPanelSnackbarMessageConstructed] = useState('');

  const [createDialogShownConstructed, setCreateDialogShownConstructed] = useState(false);
  const [renameGroupIdConstructed, setRenameGroupIdConstructed] = useState('');
  const [renameDisplayNameConstructed, setRenameDisplayNameConstructed] = useState('');
  const [manageGroupIdConstructed, setManageGroupIdConstructed] = useState('');

  const [manageTitleConstructed, setManageTitleConstructed] = useState('');
  const [manageSearchConstructed, setManageSearchConstructed] = useState('');
  const [debouncedManageSearchConstructed, setDebouncedManageSearchConstructed] =
    useState('');

  useEffect(() => {
    const qGroupsBuilt = query(
      collection(db, 'breweryGroups'),
      orderBy('displayName'),
    );

    const unsubscribeConstructed = onSnapshot(qGroupsBuilt, (snapBuilt) => {
      setBreweryGroupsListErrorConstructed('');
      const nextRowsBuilt = snapBuilt.docs.map((docSnapConstructed) => {
        const parsedDataConstructed = docSnapConstructed.data()
          ?? {};
        return {

          docIdConstructed: docSnapConstructed.id,

          displayNameConstructed:
            typeof parsedDataConstructed.displayName === 'string'
              ? parsedDataConstructed.displayName
              : '(unnamed)',
          memberCountConstructed:
            typeof parsedDataConstructed.memberCount === 'number'

              ? parsedDataConstructed.memberCount

              : 0,

        };

      });

      setGroupRowsConstructed(nextRowsBuilt);
      setBreweryGroupsFirestoreLoadingConstructed(false);
    }, (groupsSnapshotError) => {

      setGroupRowsConstructed([]);
      setBreweryGroupsFirestoreLoadingConstructed(false);
      setBreweryGroupsListErrorConstructed(
        typeof groupsSnapshotError?.message === 'string'
          && groupsSnapshotError.message.trim().length > 0

          ? groupsSnapshotError.message
          : 'Could not load brewery groups.',
      );

    });

    return () => unsubscribeConstructed();

  }, [groupsListenerRetryNonceConstructed]);

  useEffect(() => {

    const timerConstructedSecond = window.setTimeout(() => {
      setDebouncedManageSearchConstructed(manageSearchConstructed);
    }, 280);
    return () => window.clearTimeout(timerConstructedSecond);
  }, [manageSearchConstructed]);

  const {

    breweries: manageSearchHitsBuilt,

    isLoading: manageSearchLoadingConstructed,

    error: manageSearchErrorConstructed,

  } = useBreweryPortalSearch(debouncedManageSearchConstructed);

  const bumpGroupsRefreshNoticeConstructed = useCallback(() => {

    setPanelSnackbarMessageConstructed('Groups updated.');
  }, []);

  const manageDialogOpenFlagConstructed = manageGroupIdConstructed.length > 0;

  const autocompletePickMembershipBridgeConstructed = useCallback(
    async (

      breweryOptionIncomingDescribe,

      existingIdSetConstructed,

      groupFirestoreIdConstructed,

      setBusyConstructed,

      setErrorPaneConstructed,

      reloadMembersConstructed,

      onGroupsBumpConstructed,

    ) => {

      if (!breweryOptionIncomingDescribe?.id) return;

      const breweryFirestoreIdConstructed = breweryOptionIncomingDescribe.id.trim();
      if (!breweryFirestoreIdConstructed) return;
      const groupFirestoreIdNormalized = groupFirestoreIdConstructed.trim();

      if (existingIdSetConstructed.has(breweryFirestoreIdConstructed)) {
        setErrorPaneConstructed('Brewery is already attached.');
        return;
      }

      const addCallableConstructed = httpsCallable(
        functions,
        ADD_BREWERIES_TO_GROUP_CALLABLE,

      );

      setBusyConstructed(true);
      setErrorPaneConstructed('');
      try {
        await addCallableConstructed({
          groupId: groupFirestoreIdNormalized,
          breweryIds: [breweryFirestoreIdConstructed],

        });

        await reloadMembersConstructed();

        onGroupsBumpConstructed();
        setManageSearchConstructed('');
        setDebouncedManageSearchConstructed('');

      } catch (failureDescribeAdd) {

        setErrorPaneConstructed(
          failureDescribeAdd?.message || 'Could not attach brewery.',

        );

      } finally {

        setBusyConstructed(false);

      }

    },

    [],

  );

  const openManageConstructed = useCallback((rowConstructed) => {
    setManageGroupIdConstructed(rowConstructed.docIdConstructed);
    setManageTitleConstructed(rowConstructed.displayNameConstructed);
    setManageSearchConstructed('');
    setDebouncedManageSearchConstructed('');
  }, []);

  const openRenameConstructed = useCallback((rowConstructed) => {
    setRenameGroupIdConstructed(rowConstructed.docIdConstructed);
    setRenameDisplayNameConstructed(rowConstructed.displayNameConstructed);
  }, []);

  const closeRenameConstructed = useCallback(() => {
    setRenameGroupIdConstructed('');
    setRenameDisplayNameConstructed('');
  }, []);

  const showCreateFabricatedBridge = useCallback(() => {
    setCreateDialogShownConstructed(true);
  }, []);

  const hideCreateFabricatedBridgeConstructed = useCallback(() => {
    setCreateDialogShownConstructed(false);
  }, []);

  const dissolveManageBackdropBridgeConstructed = useCallback(() => {
    setManageGroupIdConstructed('');
  }, []);

  const clearSuccessSnackbarBridgeConstructed = useCallback(() => {
    setPanelSnackbarMessageConstructed('');
  }, []);

  const retryGroupsFirestoreListenBridgeConstructed = useCallback(() => {
    setBreweryGroupsListErrorConstructed('');
    setBreweryGroupsFirestoreLoadingConstructed(true);
    setGroupsListenerRetryNonceConstructed(
      (previousNonceConstructed) => previousNonceConstructed + 1,

    );

  }, []);

  return (
    <DashboardLayout title="Brewery groups">
      <Box sx={{
        p: 2,

      }}

      >
        <Box sx={{
          mb: 2,
          display: 'flex',

          gap: 1,

        }}

        >

          <Button component={RouterLink} to="/breweries" variant="outlined">

            Breweries list

          </Button>

          <Button variant="contained" onClick={showCreateFabricatedBridge}>
            Create brewery group

          </Button>

        </Box>

        {breweryGroupsListErrorConstructed ? (
          <Alert severity="error" sx={{ mb: 2 }}

            action={(

              <Button
                color="inherit"
                size="small"
                variant="text"
                onClick={retryGroupsFirestoreListenBridgeConstructed}
              >
                Retry
              </Button>

            )}
          >
            {breweryGroupsListErrorConstructed}

          </Alert>
        ) : null}

        {breweryGroupsListErrorConstructed ? null : (
          <BreweryGroupsMainAreaConstructed
            loadingFlagConstructed={breweryGroupsFirestoreLoadingConstructed}
            locateManageConstructed={openManageConstructed}
            openRenameConstructed={openRenameConstructed}

            rowsPayloadConstructed={groupRowsConstructed}

          />
        )}


        <CreateBreweryGroupDialog
          open={createDialogShownConstructed}
          onClose={hideCreateFabricatedBridgeConstructed}
          onCreated={bumpGroupsRefreshNoticeConstructed}
        />

        <RenameBreweryGroupDialog
          open={renameGroupIdConstructed.length > 0}
          groupId={renameGroupIdConstructed}
          initialDisplayName={renameDisplayNameConstructed}
          onClose={closeRenameConstructed}
          onRenamed={bumpGroupsRefreshNoticeConstructed}
        />

        <BreweryGroupMembershipDialog

          isDialogOpenConstructed={manageDialogOpenFlagConstructed}

          groupIdConstructed={manageGroupIdConstructed}

          groupTitleConstructed={manageTitleConstructed}

          onPickAutocompleteOptionBridge={autocompletePickMembershipBridgeConstructed}
          searchInputConstructed={manageSearchConstructed}

          searchHitsConstructed={manageSearchHitsBuilt}

          searchLoadingConstructed={manageSearchLoadingConstructed}

          searchErrorConstructed={manageSearchErrorConstructed}

          onSearchInputChangeBridge={setManageSearchConstructed}

          onCloseConstructed={dissolveManageBackdropBridgeConstructed}

          onGroupsChangedConstructed={bumpGroupsRefreshNoticeConstructed}

        />

        <Snackbar
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',

          }}

          autoHideDuration={4000}

          open={panelSnackbarMessageConstructed.length > 0}

          onClose={clearSuccessSnackbarBridgeConstructed}

        >

          <Alert severity="success" onClose={clearSuccessSnackbarBridgeConstructed}

          >

            {panelSnackbarMessageConstructed}

          </Alert>

        </Snackbar>

      </Box>

    </DashboardLayout>

  );

}

export default BreweryGroups;

